import { App, Flex, Space } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import {
  type AddressLinkValues,
  type AddressQuickLink,
  buildAddressQuickLinks
} from "./address-quick-links.ts"

interface AddressQuickLinksProps extends AddressLinkValues {
  className?: string
}

function AddressQuickLinks({ className, ...address }: AddressQuickLinksProps) {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const links = buildAddressQuickLinks(address)
  if (!links.length) return null

  const copy: Record<
    AddressQuickLink["id"],
    { label: string; ariaLabel: string }
  > = {
    maps: {
      label: t("addressQuickLinks.maps.label"),
      ariaLabel: t("addressQuickLinks.maps.ariaLabel")
    },
    satellite: {
      label: t("addressQuickLinks.satellite.label"),
      ariaLabel: t("addressQuickLinks.satellite.ariaLabel")
    },
    zillow: {
      label: t("addressQuickLinks.zillow.label"),
      ariaLabel: t("addressQuickLinks.zillow.ariaLabel")
    },
    redfin: {
      label: t("addressQuickLinks.redfin.label"),
      ariaLabel: t("addressQuickLinks.redfin.ariaLabel")
    },
    realtor: {
      label: t("addressQuickLinks.realtor.label"),
      ariaLabel: t("addressQuickLinks.realtor.ariaLabel")
    }
  }

  // The link still opens the site; the copy rides along on the same click.
  const copyAddress = (copyText: string) => {
    const copyFailed = () =>
      void message.warning(t("addressQuickLinks.copyFailed"))
    if (!navigator.clipboard) return copyFailed()
    navigator.clipboard
      .writeText(copyText)
      .then(
        () => void message.success(t("addressQuickLinks.addressCopied")),
        copyFailed
      )
  }

  return (
    <nav
      aria-label={t("addressQuickLinks.navigationLabel")}
      className={className}
    >
      {/* Links that open the address itself, then the site searches that
          need it pasted: each group on a line of its own. */}
      <Flex vertical gap={4}>
        {[
          links.filter((link) => !link.copyText),
          links.filter((link) => link.copyText)
        ]
          .filter((group) => group.length)
          .map((group) => (
            <Space key={group[0].id} size="small" wrap>
              {group.map((link) => {
                const text = copy[link.id]
                const { copyText } = link

                return (
                  <a
                    key={link.id}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={text.ariaLabel}
                    onClick={copyText ? () => copyAddress(copyText) : undefined}
                    className="text-primary hover:underline"
                  >
                    {text.label}
                  </a>
                )
              })}
            </Space>
          ))}
      </Flex>
    </nav>
  )
}

export { AddressQuickLinks }
